/**
 * SkillsTab — 에이전트 변환 모니터링 컨테이너 (3 sub-tab).
 *
 * 본 스펙: AIFYAGENT/MONITORING_SPEC.md §3
 *
 * 구조:
 *   상단 공통: 날짜 picker (default=어제), 30s 자동 폴링
 *   [그리드 ★기본]  [실패만]  [파이프라인]
 */

import { useCallback, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import GridViewIcon from "@mui/icons-material/GridView";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AccountTreeIcon from "@mui/icons-material/AccountTree";

import { TenantGridTab } from "./TenantGridTab";
import { FailuresTab } from "./FailuresTab";
import { PipelineSubTab } from "./PipelineSubTab";
import { yesterdayBsda } from "../helpers/pipeline";

type SubTab = "grid" | "failures" | "pipeline";

interface PipelineFocus {
  tenantId: string;
  promptId?: string;
}

// Phase-2 toggle (MONITORING_SPEC §11). OFF → 단일 PipelineSubTab (legacy);
// ON → [그리드 ★] [실패만] [파이프라인] 3 탭 구조.
const FEATURE_MONITORING_V2 =
  process.env.REACT_APP_FEATURE_MONITORING_V2 === "true";

export function SkillsTab() {
  const [bsda, setBsda] = useState<string>(yesterdayBsda());
  const [active, setActive] = useState<SubTab>(FEATURE_MONITORING_V2 ? "grid" : "pipeline");
  const [pipelineFocus, setPipelineFocus] = useState<PipelineFocus | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const onJumpToPipeline = useCallback((tenantId: string, promptId: string) => {
    setPipelineFocus({ tenantId, promptId });
    setActive("pipeline");
  }, []);

  const onRefresh = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  // YYYYMMDD ↔ YYYY-MM-DD 변환 (input type="date" 호환)
  const bsdaIso =
    bsda.length === 8 ? `${bsda.slice(0, 4)}-${bsda.slice(4, 6)}-${bsda.slice(6, 8)}` : bsda;
  const onBsdaChange = (iso: string) => setBsda(iso.replace(/-/g, ""));

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <TextField
          type="date"
          size="small"
          label="날짜 (BSDA)"
          value={bsdaIso}
          onChange={(e) => onBsdaChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 180 }}
        />
        <Tooltip title="새로고침 (자동 30초)">
          <IconButton size="small" onClick={onRefresh}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Typography variant="caption" color="text.secondary">
          30초 자동 폴링
        </Typography>
      </Stack>

      {FEATURE_MONITORING_V2 ? (
        <>
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
            <Tabs value={active} onChange={(_e, v: SubTab) => setActive(v)}>
              <Tab
                icon={<GridViewIcon fontSize="small" />}
                iconPosition="start"
                value="grid"
                label="테넌트 그리드"
              />
              <Tab
                icon={<ErrorOutlineIcon fontSize="small" />}
                iconPosition="start"
                value="failures"
                label="실패만"
              />
              <Tab
                icon={<AccountTreeIcon fontSize="small" />}
                iconPosition="start"
                value="pipeline"
                label="파이프라인"
              />
            </Tabs>
          </Box>

          {active === "grid" && <TenantGridTab key={`grid-${bsda}-${refreshKey}`} bsda={bsda} />}
          {active === "failures" && (
            <FailuresTab
              key={`failures-${bsda}-${refreshKey}`}
              bsda={bsda}
              onJumpToPipeline={onJumpToPipeline}
            />
          )}
          {active === "pipeline" && (
            <PipelineSubTab
              key={`pipeline-${pipelineFocus?.tenantId || ""}-${refreshKey}`}
              initialTenantId={pipelineFocus?.tenantId}
              initialBsda={bsda}
            />
          )}
        </>
      ) : (
        // V2 OFF — Phase 1 호환 (legacy): PipelineSubTab 만 노출.
        <PipelineSubTab
          key={`pipeline-${pipelineFocus?.tenantId || ""}-${refreshKey}`}
          initialTenantId={pipelineFocus?.tenantId}
          initialBsda={bsda}
        />
      )}
    </Box>
  );
}
